import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";

const LINKS = [
  { label: "Company", href: "#company" },
  { label: "Services", href: "#services" },
  { label: "Customers", href: "#industries" },
  { label: "Leadership", href: "#leadership" },
  { label: "Contact Us", href: "#contact" },
];

const linkId = (label) => label.toLowerCase().replace(/\s+/g, "-");

const scrollTo = (e, href) => {
  e.preventDefault();
  if (window.__lenis) {
    window.__lenis.scrollTo(href, { offset: -64 });
  } else {
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  }
};

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!window.__lenis) return;
    if (open) window.__lenis.stop();
    else window.__lenis.start();
  }, [open]);

  return (
    <>
    <header
      data-testid="site-nav"
      className={`fixed inset-x-0 top-0 z-50 border-b backdrop-blur-xl transition-colors duration-500 ${
        scrolled ? "border-white/10 bg-black/70" : "border-transparent bg-black/30"
      }`}
    >
      <div className="mx-auto flex max-w-[1440px] items-center justify-between px-6 py-4 md:px-12">
        <a
          href="#top"
          onClick={(e) => scrollTo(e, "#top")}
          data-testid="nav-logo"
          className="flex items-center"
        >
          <img
            src="/assets/logo.png"
            alt="ElectroHaul — Empowering the Future of Freight"
            className="h-12 w-auto md:h-14"
          />
        </a>

        <nav className="hidden items-center gap-8 md:flex">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={(e) => scrollTo(e, l.href)}
              data-testid={`nav-link-${linkId(l.label)}`}
              className="font-mono text-[11px] uppercase tracking-[0.2em] text-smoke transition-colors duration-300 hover:text-volt"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <button
          onClick={() => setOpen(true)}
          data-testid="nav-mobile-toggle"
          aria-label="Open navigation menu"
          className="text-white transition-colors hover:text-volt md:hidden"
        >
          <Menu className="h-6 w-6" />
        </button>
      </div>
    </header>

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              key="backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={() => setOpen(false)}
              className="fixed inset-0 z-[55] bg-black/70 backdrop-blur-sm"
            />
            <motion.aside
              key="panel"
              data-testid="nav-mobile-panel"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="fixed inset-y-0 right-0 z-[60] flex w-[86%] max-w-sm flex-col border-l border-line bg-coal"
            >
              <div className="flex items-center justify-between border-b border-line px-6 py-5">
                <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-volt">
                  Menu
                </span>
                <button
                  onClick={() => setOpen(false)}
                  data-testid="nav-mobile-close"
                  aria-label="Close navigation menu"
                  className="text-white transition-colors hover:text-volt"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              <nav className="flex flex-1 flex-col px-6 py-6">
                {LINKS.map((l, i) => (
                  <motion.a
                    key={l.href}
                    href={l.href}
                    initial={{ opacity: 0, x: 28 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.15 + i * 0.06, duration: 0.4 }}
                    onClick={(e) => {
                      e.preventDefault();
                      const href = l.href;
                      setOpen(false);
                      setTimeout(() => {
                        if (window.__lenis) window.__lenis.scrollTo(href, { offset: -64 });
                        else document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
                      }, 350);
                    }}
                    data-testid={`nav-mobile-link-${linkId(l.label)}`}
                    className="border-b border-line py-5 font-display text-3xl font-medium tracking-tight text-white transition-colors duration-300 hover:text-volt"
                  >
                    {l.label}
                  </motion.a>
                ))}
              </nav>
              <p className="px-6 pb-8 font-mono text-[10px] uppercase tracking-[0.25em] text-smoke">
                Empowering the Future of Freight
              </p>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
